import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { addSoup, removeSoup, limpiarCarrito } from "../../reducer/actions";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toast, ToastContainer } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import styles from "./Carrito.module.css";
import Footer from "../Footer/Footer";
import FooterMobile from "../Footer/FooterMobile";
import NavBarMobile from "../NavBar/NavBarMobile";
import NavBarMinimized from "../NavBar/NavBarMinimized ";
import MercadoPago from "../MercadoPago/MercadoPago";

const Carrito = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const kitsCompra = useSelector((state) => state.kitsCompra);
  const kits = useSelector((state) => state.kits);

  // State management
  const [shippingData, setShippingData] = useState(null); // For shipping information
  const [preferenceData, setPreferenceData] = useState(null); // For Mercado Pago preference
  const [isLoadingShipping, setIsLoadingShipping] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("Error"); // "Éxito" o "Error"
  const [billingInfo, setBillingInfo] = useState(
    JSON.parse(localStorage.getItem("billingInfo")) || {
      name: "",
      surname: "",
      phone: "",
    }
  );
  const [shippingInfo, setShippingInfo] = useState(
    JSON.parse(localStorage.getItem("shippingInfo")) || {
      postalCode: "",
      email: "",
      street: "",
      crossStreetsAndNeighborhood: "",
    }
  );
  const [errors, setErrors] = useState({
    phone: "",
    postalCode: "",
    email: "",
    name: "",
    surname: "",
    crossStreetsAndNeighborhood: "",
  });
  const [billingComplete, setBillingComplete] = useState(false);
  const [shippingComplete, setShippingComplete] = useState(false);
  const [showMercadoPago, setShowMercadoPago] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const hasProcessedPayment = useRef(false);

  // Detect mobile view
  const isMobile = window.innerWidth <= 820 || window.innerHeight <= 590;

  // Enviar evento view_cart a Google Tag Manager al montar el componente
  useEffect(() => {
    if (kitsCompra.length > 0) {
      const productos = kitsCompra
        .flat()
        .filter((soup) => soup && soup.id && soup.name && soup.price);
      if (productos.length > 0) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "view_cart",
          ecommerce: {
            items: productos.map((product) => ({
              item_name: product.name,
              item_id: product.id,
              price: Number(product.price) || 0,
              quantity: 1,
              item_category: "Sopas",
            })),
          },
        });
        console.log("Evento view_cart enviado:", productos);
      }
    }
  }, [kitsCompra]);

  // Helper function to restore data from multiple sources
  const restoreData = (key) => {
    const sources = [
      { storage: sessionStorage, name: `${key} (sessionStorage)`, key },
      { storage: localStorage, name: `${key} (localStorage)`, key },
      {
        storage: localStorage,
        name: `${key} (mercadoPagoPreference)`,
        key: "mercadoPagoPreference",
        extractor: (data) => data?.orderData,
      },
    ];

    for (const source of sources) {
      const itemKey = source.key || key;
      const data = source.storage.getItem(itemKey);
      if (data) {
        try {
          const parsed = JSON.parse(data);
          const result = source.extractor ? source.extractor(parsed) : parsed;
          if (result && Object.keys(result).length > 0) {
            console.log(`Restored ${source.name}:`, result);
            return result;
          }
        } catch (error) {
          console.error(`Error parsing ${source.name}:`, error);
        }
      }
    }
    console.warn(`No valid data found for ${key}`);
    return null;
  };

  // Restore kitsCompra from localStorage on mount
  useEffect(() => {
    if (kitsCompra.length === 0) {
      const savedKits = restoreData("kitsCompra");
      if (savedKits && Array.isArray(savedKits)) {
        try {
          dispatch(limpiarCarrito());
          savedKits.forEach((kit) => {
            if (Array.isArray(kit)) {
              kit.forEach((soup) => dispatch(addSoup(soup)));
            }
          });
        } catch (error) {
          console.error("Error restoring kitsCompra:", error);
          setToastMessage("Error al restaurar el carrito.");
          setToastType("Error");
          setShowToast(true);
        }
      }
    }
  }, [dispatch, kitsCompra.length]);

  // Save kitsCompra to localStorage whenever it changes
  useEffect(() => {
    try {
      const kitsString = JSON.stringify(kitsCompra);
      localStorage.setItem("kitsCompra", kitsString);
      sessionStorage.setItem("kitsCompra", kitsString);
      console.log("kitsCompra saved to storage:", kitsCompra);
    } catch (error) {
      console.error("Error saving kitsCompra to storage:", error);
      setToastMessage("Error al guardar el carrito.");
      setToastType("Error");
      setShowToast(true);
    }
  }, [kitsCompra]);

  // Calculate cart status
  const stepOneComplete =
    kitsCompra.length > 0 && kitsCompra.every((kit) => kit.length === 5);
  const isCartComplete = stepOneComplete && billingComplete && shippingComplete;
  const numberOfKits = kitsCompra.length;

  // Calculate total and apply discounts
  const calculateTotal = useCallback(() => {
    console.log("kitsCompra:", kitsCompra);
    console.log("kits:", kits);
    console.log("shippingData:", shippingData);

    let shippingCost = 0;
    if (
      shippingData &&
      !isLoadingShipping &&
      shippingData?.[0]?.envio !== false &&
      !(
        shippingData?.[0]?.envioGratis &&
        shippingData[0].envioGratis <= numberOfKits
      )
    ) {
      shippingCost = Number(shippingData?.[0]?.precio) || 0;
    }
    if (shippingData?.[0]?.envio === false) {
      shippingCost = 0;
    }

    let subtotal = 0;
    if (!Array.isArray(kitsCompra) || kitsCompra.length === 0) {
      console.warn("kitsCompra is empty or not an array, subtotal set to 0");
      return { total: shippingCost, shippingCost, subtotal };
    }

    subtotal = kitsCompra.reduce((sum, kit) => {
      if (!Array.isArray(kit)) {
        console.warn("Invalid kit, skipping:", kit);
        return sum;
      }

      const matchingKit = Array.isArray(kits)
        ? kits.find((predefinedKit) => {
            if (
              !predefinedKit?.products ||
              !Array.isArray(predefinedKit.products) ||
              kit.length !== 5
            ) {
              return false;
            }
            const kitIds = kit
              .map((soup) => soup?.id)
              .filter((id) => id != null)
              .sort();
            const predefinedKitIds = predefinedKit.products
              .map((soup) => soup?.id)
              .filter((id) => id != null)
              .sort();
            return JSON.stringify(kitIds) === JSON.stringify(predefinedKitIds);
          })
        : null;

      if (matchingKit && Number(matchingKit.price)) {
        console.log(`Kit matched, using price: ${matchingKit.price}`);
        return sum + Number(matchingKit.price);
      } else {
        const kitTotal = kit.reduce((kitSum, soup) => {
          const price = Number(soup?.price) || 0;
          console.log(`Soup price: ${price}`);
          return kitSum + price;
        }, 0);
        console.log(`No matching kit, using kit total: ${kitTotal}`);
        return sum + kitTotal;
      }
    }, 0);

    const total = subtotal + shippingCost;
    console.log(
      `Total calculated: ${total} (subtotal: ${subtotal}, shipping: ${shippingCost})`
    );

    return { total, shippingCost, subtotal };
  }, [kitsCompra, kits, shippingData, numberOfKits, isLoadingShipping]);

  const { total, shippingCost } = useMemo(
    () => calculateTotal() || { total: 0, shippingCost: 0, subtotal: 0 },
    [calculateTotal]
  );

  // Fetch shipping data
  useEffect(() => {
    if (kitsCompra.length > 0) {
      setIsLoadingShipping(true);
      axios
        .get("https://rezuma-back.onrender.com/ship/shipping")
        .then((response) => {
          if (!response.data || response.data.length === 0) {
            setShippingData([{ envio: false, precio: 3500, envioGratis: 2 }]);
          } else {
            setShippingData(response.data);
          }
        })
        .catch((error) => {
          console.error("Error fetching shipping data:", error);
          setToastMessage(
            "Error al obtener datos de envío. Usando valores predeterminados."
          );
          setToastType("Error");
          setShowToast(true);
          setShippingData([{ envio: false, precio: 3500, envioGratis: 2 }]);
        })
        .finally(() => {
          setIsLoadingShipping(false);
        });
    } else {
      setShippingData(null);
      setShowMercadoPago(false);
      setIsLoadingShipping(false);
    }
  }, [kitsCompra]);

  // Update billing completion status
  useEffect(() => {
    const { name, surname, phone } = billingInfo;
    const isComplete = !!name.trim() && !!surname.trim() && !!phone.trim();
    setBillingComplete(isComplete);
    try {
      localStorage.setItem("billingInfo", JSON.stringify(billingInfo));
    } catch (error) {
      console.error("Error saving billingInfo to localStorage:", error);
    }

    setErrors((prev) => ({
      ...prev,
      name: name.trim() ? "" : "El nombre es obligatorio",
      surname: surname.trim() ? "" : "El apellido es obligatorio",
    }));
  }, [billingInfo]);

  // Update shipping completion status
  useEffect(() => {
    const { postalCode, email, street } = shippingInfo;
    const isComplete = !!postalCode.trim() && !!email.trim() && !!street.trim();

    setShippingComplete(isComplete);
    try {
      localStorage.setItem("shippingInfo", JSON.stringify(shippingInfo));
    } catch (error) {
      console.error("Error saving shippingInfo to localStorage:", error);
    }
  }, [shippingInfo]);

  // Process payment response from Mercado Pago
  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const paymentResponse = {
      collection_id: query.get("collection_id"),
      collection_status: query.get("collection_status"),
      payment_id: query.get("payment_id"),
      status: query.get("status"),
      external_reference: query.get("external_reference"),
      payment_type: query.get("payment_type"),
      merchant_order_id: query.get("merchant_order_id"),
      preference_id: query.get("preference_id"),
      site_id: query.get("site_id"),
      processing_mode: query.get("processing_mode"),
      merchant_account_id: query.get("merchant_account_id"),
    };

    // Si no hay parámetros, salir
    if (!Object.values(paymentResponse).some((value) => value)) {
      return;
    }

    // Limpiar la URL inmediatamente para evitar bucles
    navigate("/Carrito", { replace: true });

    // Verificar si el pago ya fue procesado
    const processedPayments =
      JSON.parse(localStorage.getItem("processedPayments")) || {};
    if (
      paymentResponse.external_reference &&
      processedPayments[paymentResponse.external_reference]
    ) {
      console.log(`Pago ${paymentResponse.external_reference} ya procesado.`);
      return;
    }

    // Procesar solo si no se ha procesado antes
    if (!hasProcessedPayment.current && paymentResponse.status) {
      hasProcessedPayment.current = true;

      // Marcar el pago como procesado en el frontend
      if (paymentResponse.external_reference) {
        processedPayments[paymentResponse.external_reference] = true;
        localStorage.setItem(
          "processedPayments",
          JSON.stringify(processedPayments)
        );
      }

      // Procesar el resultado del pago (solo UI)
      try {
        if (paymentResponse.status === "approved") {
          console.log("Pago aprobado, actualizando UI...");
          // Enviar evento purchase a Google Tag Manager
          const productos = kitsCompra
            .flat()
            .filter((soup) => soup && soup.id && soup.name && soup.price);
          if (productos.length > 0) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: "purchase",
              ecommerce: {
                transaction_id:
                  paymentResponse.external_reference || `ORDER_${Date.now()}`,
                value: Number(total) || 0,
                currency: "ARS",
                shipping: Number(shippingCost) || 0,
                items: productos.map((product) => ({
                  item_name: product.name,
                  item_id: product.id,
                  price: Number(product.price) || 0,
                  quantity: 1,
                  item_category: "Sopas",
                })),
              },
            });
            console.log("Evento purchase enviado:", {
              transaction_id: paymentResponse.external_reference,
              value: total,
              currency: "ARS",
              shipping: shippingCost,
              items: productos,
            });
          }

          dispatch(limpiarCarrito());
          localStorage.removeItem("kitsCompra");
          sessionStorage.removeItem("kitsCompra");
          localStorage.removeItem("billingInfo");
          localStorage.removeItem("shippingInfo");
          localStorage.removeItem("mercadoPagoPreference");
          localStorage.removeItem("orderData");
          sessionStorage.removeItem("orderData");
          setToastMessage(
            "¡Compra realizada con éxito! Gracias por tu pedido."
          );
          setToastType("Éxito");
          setShowToast(true);
          setShowModal(true);
          console.log("Frontend UI actualizada");
        } else if (
          paymentResponse.status === "rejected" ||
          paymentResponse.status === "cancelled"
        ) {
          console.error("Pago rechazado o cancelado:", paymentResponse.status);
          setToastMessage(
            "El pago fue rechazado o cancelado. Por favor, intenta nuevamente."
          );
          setToastType("Error");
          setShowToast(true);
          hasProcessedPayment.current = false;
        } else if (paymentResponse.status === "pending") {
          console.log("Pago pendiente, mostrando mensaje...");
          setToastMessage(
            "El pago está pendiente. Te notificaremos cuando se procese."
          );
          setToastType("Éxito");
          setShowToast(true);
          hasProcessedPayment.current = false;
        }
      } catch (error) {
        console.error("Error procesando el pago en el frontend:", error);
        setToastMessage(
          "Error inesperado al procesar el pedido. Contacta al soporte."
        );
        setToastType("Error");
        setShowToast(true);
        hasProcessedPayment.current = false;
      }
    }
  }, [dispatch, navigate, kitsCompra, total, shippingCost]);

  // Auto-close modal and reset state
  useEffect(() => {
    if (showModal) {
      const timer = setTimeout(() => {
        setShowModal(false);
        hasProcessedPayment.current = false; // Reset for new payments
        navigate("/Carrito", { replace: true });
      }, 15000);
      return () => clearTimeout(timer);
    }
  }, [showModal, navigate]);

  // Input validation for billing info
  const handleBillingInputChange = useCallback((event) => {
    const { name, value } = event.target;
    setBillingInfo((prev) => ({ ...prev, [name]: value }));

    if (name === "phone") {
      setErrors((prev) => ({
        ...prev,
        phone: /^\d{10}$/.test(value)
          ? ""
          : "El teléfono debe contener 10 dígitos",
      }));
    } else if (name === "name") {
      setErrors((prev) => ({
        ...prev,
        name: value.trim() ? "" : "El nombre es obligatorio",
      }));
    } else if (name === "surname") {
      setErrors((prev) => ({
        ...prev,
        surname: value.trim() ? "" : "El apellido es obligatorio",
      }));
    }
  }, []);

  // Input validation for shipping info
  const handleShippingInputChange = useCallback((event) => {
    const { name, value } = event.target;
    setShippingInfo((prev) => ({ ...prev, [name]: value }));

    if (name === "postalCode") {
      setErrors((prev) => ({
        ...prev,
        postalCode:
          /^[0-9]{4,10}$/.test(value) || value === ""
            ? ""
            : "El código postal debe contener solo números (4-10 dígitos)",
      }));
    } else if (name === "email") {
      setErrors((prev) => ({
        ...prev,
        email:
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || value === ""
            ? ""
            : "Por favor, ingrese un email válido",
      }));
    } else if (name === "crossStreetsAndNeighborhood") {
      setErrors((prev) => ({
        ...prev,
        crossStreetsAndNeighborhood: value.trim()
          ? ""
          : "Entre calles y barrio es obligatorio",
      }));
    }
  }, []);

  // Handle form submission for Checkout Pro
  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      setIsSubmitting(true);

      if (!isCartComplete) {
        setToastMessage(
          "Completa todos los campos y asegúrate de tener 5 sopas por kit."
        );
        setToastType("Error");
        setShowToast(true);
        setIsSubmitting(false);
        return;
      }

      if (Object.values(errors).some((error) => error)) {
        setToastMessage(
          "Corrige los errores en los campos antes de continuar."
        );
        setToastType("Error");
        setShowToast(true);
        setIsSubmitting(false);
        return;
      }

      try {
        let shippingCost = 0;
        if (
          shippingData &&
          shippingData?.[0]?.envio !== false &&
          !(
            shippingData?.[0]?.envioGratis &&
            shippingData[0].envioGratis <= numberOfKits
          )
        ) {
          shippingCost = Number(shippingData?.[0]?.precio) || 0;
        }

        const orderData = {
          title: "ReZuma",
          quantity: 1,
          unit_price: total,
          billingInfo,
          shippingInfo: {
            postalCode: shippingInfo.postalCode,
            email: shippingInfo.email,
            street: shippingInfo.street,
            crossStreetsAndNeighborhood:
              shippingInfo.crossStreetsAndNeighborhood,
          },
          kitsCompra,
          shippingCost,
        };

        const preference = {
          items: [
            {
              title: orderData.title,
              unit_price: Number(orderData.unit_price),
              quantity: orderData.quantity,
              currency_id: "ARS",
            },
          ],
          payer: {
            first_name: billingInfo?.name || "Unknown",
            last_name: billingInfo?.surname || "Unknown",
            email: shippingInfo?.email || "default@example.com",
            phone: {
              number:
                Number((billingInfo?.phone || "").replace(/\D/g, "")) ||
                1234567890,
            },
            address: {
              street_name: shippingInfo?.street || "Unknown",
              street_number: 123,
              zip_code: shippingInfo?.postalCode || "1000",
              crossStreetsAndNeighborhood:
                shippingInfo?.crossStreetsAndNeighborhood || "",
            },
          },
          payment_methods: {
            excluded_payment_types: [{ id: "ticket" }],
            excluded_payment_methods: [
              { id: "oxxo" },
              { id: "rapipago" },
              { id: "pagofacil" },
              { id: "efecty" },
            ],
            installments: 1,
          },
          back_urls: {
            success: "https://www.rezuma.com.ar/Carrito",
            failure: "https://www.rezuma.com.ar/Carrito",
            pending: "https://www.rezuma.com.ar/Carrito",
          },
          auto_return: "approved",
          external_reference: `ORDER_${Date.now()}`,
          metadata: { orderData },
        };

        console.log(
          "Preference to be sent:",
          JSON.stringify(preference, null, 2)
        );

        // Save kitsCompra to localStorage and sessionStorage
        try {
          const kitsString = JSON.stringify(kitsCompra);
          localStorage.setItem("kitsCompra", kitsString);
          sessionStorage.setItem("kitsCompra", kitsString);
          console.log(
            "kitsCompra saved to localStorage and sessionStorage:",
            kitsCompra
          );
        } catch (error) {
          console.error("Error saving kits to storage:", error);
        }

        const response = await axios.post(
          "https://rezuma-back.onrender.com/MercadoPago/create_preference",
          preference
        );

        if (!response.data.id) {
          throw new Error("No preference ID returned from backend");
        }

        const preferenceData = {
          id: response.data.id,
          orderData,
        };
        try {
          const preferenceString = JSON.stringify(preferenceData);
          localStorage.setItem("mercadoPagoPreference", preferenceString);
          sessionStorage.setItem("mercadoPagoPreference", preferenceString);
          console.log("mercadoPagoPreference saved:", preferenceData);
        } catch (error) {
          console.error("Error saving mercadoPagoPreference:", error);
        }

        setPreferenceData(preferenceData);
        setShowMercadoPago(true);
        console.log("Preference ID received:", response.data.id);
      } catch (error) {
        console.error("Error creating preference:", error);
        let errorMessage = "Error al procesar el pago. Intenta nuevamente.";
        if (error.code === "ERR_NETWORK") {
          errorMessage =
            "No se pudo conectar con el servidor. Verifica tu conexión.";
        } else if (error.response?.status === 403) {
          errorMessage = "Acceso denegado. Verifica las credenciales.";
        } else if (error.response?.status === 404) {
          errorMessage = "Endpoint no encontrado.";
        }
        setToastMessage(errorMessage);
        setToastType("Error");
        setShowToast(true);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      isCartComplete,
      errors,
      total,
      billingInfo,
      shippingInfo,
      kitsCompra,
      numberOfKits,
      shippingData,
    ]
  );

  // Render shipping cost information
  const renderShippingInfo = useCallback(() => {
    if (isLoadingShipping || !kitsCompra.length || !shippingData) {
      return <h4 className="text-start">Cargando información de envío...</h4>;
    }

    if (
      shippingData?.[0]?.envio === false ||
      (shippingData?.[0]?.envioGratis &&
        shippingData[0].envioGratis <= numberOfKits)
    ) {
      return <h4 className="text-start text-success">¡Envío Gratis!</h4>;
    }

    const shippingCost = Number(shippingData?.[0]?.precio) || 0;
    return (
      <h4 className="text-start">Costo de Envío: ${shippingCost.toFixed(2)}</h4>
    );
  }, [shippingData, numberOfKits, isLoadingShipping, kitsCompra.length]);

  const handleModalButtonClick = () => {
    setShowModal(false);
    hasProcessedPayment.current = false; // Reset for new payments
    navigate("/Carrito", { replace: true });
  };

  return (
    <div className={styles.cartContainer}>
      <ToastContainer
        className={`position-fixed p-3 ${styles.customtoastcontainer}`}
        style={{ top: "35%", right: "35%" }}
      >
        <Toast
          className={styles.customtoastcontainer2}
          onClose={() => setShowToast(false)}
          show={showToast}
          delay={5000}
          autohide
        >
          <Toast.Header>
            <strong className="me-auto">{toastType}</strong>
          </Toast.Header>
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>

      {showModal && (
        <div className={styles.modalBackground}>
          <div className={styles.modal}>
            <p>¡Gracias por tu compra!</p>
            <button onClick={handleModalButtonClick}>Aceptar</button>
          </div>
        </div>
      )}

      {isMobile ? <NavBarMobile /> : <NavBarMinimized />}

      <div className="container mt-5">
        <div className="row mb-4">
          <div className="col-12">
            <div className={styles["step-container"]}>
              <div
                className={`${styles.step} ${
                  stepOneComplete ? styles.complete : styles.incomplete
                }`}
              >
                1<div className={styles.stepLabel}>Elegí tus productos</div>
              </div>
              <div
                className={`${styles.step} ${
                  billingComplete ? styles.complete : styles.incomplete
                }`}
              >
                2
                <div className={styles.stepLabel}>
                  Completá tus datos de contacto
                </div>
              </div>
              <div
                className={`${styles.step} ${
                  shippingComplete ? styles.complete : styles.incomplete
                }`}
              >
                3
                <div className={styles.stepLabel}>
                  Completá los datos de envío
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={`row ${styles.color}`}>
          <div className="col-lg-6">
            <div className={`p-4 border-0 ${styles.color}`}>
              <h2 className="mb-4">Carrito</h2>
              <div className={styles.soupContainer}>
                {kitsCompra.length === 0 ? (
                  <p>Tu carrito está vacío.</p>
                ) : (
                  kitsCompra.map(
                    (kit, kitIndex) =>
                      kit.length > 0 && (
                        <div key={kitIndex} className="mb-4">
                          <h3>Kit {kitIndex + 1}</h3>
                          {kit.map((soup, soupIndex) => (
                            <div
                              key={soupIndex}
                              className="d-flex align-items-center mb-3 border-bottom pb-2"
                            >
                              <img
                                src={soup.images?.[0] || "placeholder.jpg"}
                                alt={soup.name || "Producto"}
                                className="border-0 img-thumbnail me-3"
                                style={{
                                  width: "100px",
                                  height: "auto",
                                  background: "#E8E1D9",
                                }}
                                onError={(e) =>
                                  (e.target.src = "placeholder.jpg")
                                }
                              />
                              <div className="me-3">
                                <span className="fw-bold">
                                  {soup.name || "Producto sin nombre"}
                                </span>
                              </div>
                              <span className="me-3">
                                ${(Number(soup.price) || 0).toFixed(2)}
                              </span>
                              <button
                                onClick={() =>
                                  dispatch(removeSoup(kitIndex, soupIndex))
                                }
                                className={`btn btn-danger ${styles.color3}`}
                              >
                                X
                              </button>
                            </div>
                          ))}
                        </div>
                      )
                  )
                )}
                {!stepOneComplete && kitsCompra.length > 0 && (
                  <p className="text-danger">
                    Debes agregar un total de 5 sopas por kit.
                  </p>
                )}
              </div>
              {renderShippingInfo()}
              <div className={styles.total}>
                <h4>Total: ${(Number(total) || 0).toFixed(2)}</h4>
              </div>
              <div className={styles.buttons}>
                <Link to="/Shop">
                  <button
                    onClick={() => dispatch(addSoup())}
                    className={styles.button2}
                  >
                    Agregar Sopa
                  </button>
                </Link>
              </div>
            </div>
          </div>

          <div className={`col-lg-6 mb-4 ${styles.color2}`}>
            <div className={`p-4 border-0 ${styles.color}`}>
              <h3 className="mb-4">Datos de Contacto</h3>
              <input
                type="text"
                name="name"
                className={`form-control mb-2 ${
                  errors.name ? "is-invalid" : ""
                }`}
                placeholder="Nombre"
                value={billingInfo.name}
                onChange={handleBillingInputChange}
                required
              />
              {errors.name && (
                <div className="invalid-feedback">{errors.name}</div>
              )}
              <input
                type="text"
                name="surname"
                className={`form-control mb-2 ${
                  errors.surname ? "is-invalid" : ""
                }`}
                placeholder="Apellido"
                value={billingInfo.surname}
                onChange={handleBillingInputChange}
                required
              />
              {errors.surname && (
                <div className="invalid-feedback">{errors.surname}</div>
              )}
              <input
                type="text"
                name="phone"
                className={`form-control mb-2 ${
                  errors.phone ? "is-invalid" : ""
                }`}
                placeholder="Teléfono (10 dígitos)"
                value={billingInfo.phone}
                onChange={handleBillingInputChange}
                required
              />
              {errors.phone && (
                <div className="invalid-feedback">{errors.phone}</div>
              )}

              <h3 className="mt-4 mb-4">Información de Envío</h3>
              <input
                type="text"
                name="postalCode"
                className={`form-control mb-2 ${
                  errors.postalCode ? "is-invalid" : ""
                }`}
                placeholder="Código Postal"
                value={shippingInfo.postalCode}
                onChange={handleShippingInputChange}
                required
              />
              {errors.postalCode && (
                <div className="invalid-feedback">{errors.postalCode}</div>
              )}
              <input
                type="email"
                name="email"
                className={`form-control mb-2 ${
                  errors.email ? "is-invalid" : ""
                }`}
                placeholder="Email"
                value={shippingInfo.email}
                onChange={handleShippingInputChange}
                required
              />
              {errors.email && (
                <div className="invalid-feedback">{errors.email}</div>
              )}
              <input
                type="text"
                name="street"
                className="form-control mb-2"
                placeholder="Calle, altura, piso/depto"
                value={shippingInfo.street}
                onChange={handleShippingInputChange}
                required
              />
              <input
                type="text"
                name="crossStreetsAndNeighborhood"
                className={`form-control mb-2 ${
                  errors.crossStreetsAndNeighborhood ? "is-invalid" : ""
                }`}
                placeholder="Entre calles y barrio"
                value={shippingInfo.crossStreetsAndNeighborhood}
                onChange={handleShippingInputChange}
                required
              />
              {errors.crossStreetsAndNeighborhood && (
                <div className="invalid-feedback">
                  {errors.crossStreetsAndNeighborhood}
                </div>
              )}

              <div className={styles.sectionp}>
                <p className={styles.p}>
                  Envíos CABA: Lunes de 10 hs. a 14 hs.
                </p>
                <p className={styles.p}>
                  Envíos Zona Norte: Miércoles de 10 hs. a 14 hs.
                </p>
                <p className={styles.p}>
                  Pedidos hasta 24 horas antes del día de envío, de lo contrario
                  se envían la próxima semana.
                </p>
              </div>

              <div className={styles.rere}>
                <button
                  className={styles.button1}
                  onClick={handleSubmit}
                  disabled={isSubmitting || !isCartComplete}
                >
                  {isSubmitting ? "Procesando..." : "Comprar"}
                </button>
              </div>

              {showMercadoPago && isCartComplete && preferenceData?.id && (
                <MercadoPago
                  data={{ preferenceId: preferenceData.id }}
                  orderData={preferenceData.orderData}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.footer}>
        {isMobile ? <FooterMobile /> : <Footer />}
      </div>
    </div>
  );
};

export default Carrito;
